import React, { useState } from "react";
import { msgid, ngettext, t } from "ttag";

import { Themed } from "../../../styles/themes";
import { ButtonPrimary, ButtonSecondary, Clickable } from "../../Clickables";
import { Svg } from "../../Svg";
import { Tooltip } from "../../Tooltip";
import { ConnectDealerModal } from "../ConnectDealerModal/ConnectDealerModal";
import { useProductContext } from "../ProductContext/ProductContext";
import type { MergedProduct } from "../ProductContext/merge";
import { SpokeContributionsList } from "../SpokeContributionsList/SpokeContributionsList";
import { SuiteSummary } from "../SuiteSummary/SuiteSummary";
import {
    Action,
    AppState,
    SKU,
    SuiteState,
    computePricing,
    discountRules,
    getJoiningKitTargets,
    hub,
} from "../engine";
import { JOINING_KIT_SKU } from "../format";

import styles from "./PopulatedLanding.module.scss";

/**
 * Suite-to-mailto serializer. Builds a `mailto:` URL whose body is a
 * plain-text summary of the suite, grouped by spoke.
 */
const buildMailtoLink = (
    suite: SuiteState,
    pricing: ReturnType<typeof computePricing>,
    catalog: Map<SKU, MergedProduct>,
): string => {
    const kitTargetIds = getJoiningKitTargets(suite);
    const skuLine = (sku: SKU, indent: string) => {
        const product = catalog.get(sku);
        if (!product) return `${indent}${sku}`;
        return `${indent}${sku}: ${product.name} (${product.msrp.formatted})`;
    };
    const sections = hub.spokes
        .map((spoke) => {
            const inSpoke = suite.contributions.filter(
                (c) => c.spoke === spoke.id,
            );
            if (inSpoke.length === 0) return null;
            const lines = inSpoke.flatMap((c) => [
                skuLine(c.mainSku, "  "),
                ...c.accessorySkus.map((sku) => skuLine(sku, "    + ")),
                ...(kitTargetIds.has(c.id)
                    ? [skuLine(JOINING_KIT_SKU, "    + ")]
                    : []),
            ]);
            return [spoke.label, ...lines].join("\n");
        })
        .filter((s): s is string => s !== null);

    const ratePct = Math.round(discountRules[0].discountRate * 100);
    const totals = [
        t`Total: ${pricing.subtotal.formatted}`,
        ...(Number(pricing.savings.amount) > 0
            ? [
                  t`Instant ${ratePct}% Savings: ${pricing.savings.formatted}`,
                  t`Total With Savings: ${pricing.total.formatted}`,
              ]
            : []),
    ].join("\n");

    const count = suite.contributions.length;
    const heading = ngettext(
        msgid`Your Dream Suite (${count} appliance)`,
        `Your Dream Suite (${count} appliances)`,
        count,
    );

    const body = [heading, ...sections, totals].join("\n\n");
    return `mailto:?subject=${encodeURIComponent(heading)}&body=${encodeURIComponent(body)}`;
};

export type PopulatedLandingProps = {
    state: AppState;
    dispatch: React.Dispatch<Action>;
    onShowOfferDetails: () => void;
};

export const PopulatedLanding = ({
    state,
    dispatch,
    onShowOfferDetails,
}: PopulatedLandingProps) => {
    const { catalog, locale } = useProductContext();
    const pricing = computePricing(state.suite, catalog, locale);
    const [dealerModalOpen, setDealerModalOpen] = useState(false);

    const emailLabel = t`Send via email.`;
    const printLabel = t`Print this page.`;
    /* v8 ignore next -- @preserve */
    const printPage = () => window.print();

    const mailtoLink = buildMailtoLink(state.suite, pricing, catalog);

    return (
        <section className={styles.root} data-testid="populated-landing">
            <header className={styles.header}>
                <h1 className={styles.heading}>{t`Your Dream Suite Awaits`}</h1>
                <p className={styles.body}>
                    {t`
                        You’re one step closer to your dream kitchen. Continue
                        building your suite below or visit your local showroom
                        to bring SKS to your home.
                    `}
                </p>
                <div className={styles.actionsRow}>
                    <ButtonSecondary onClick={() => setDealerModalOpen(true)}>
                        {t`Connect with a Dealer`}
                    </ButtonSecondary>
                    <ButtonPrimary
                        onClick={() => dispatch({ type: "OPEN_HUB" })}
                    >
                        {t`Add an Appliance`}
                    </ButtonPrimary>
                </div>
            </header>
            <Themed theme="white" className={styles.page}>
                <SuiteSummary
                    suite={state.suite}
                    onShowSavingsDetails={onShowOfferDetails}
                    className={styles.statusBar}
                    actions={
                        <div className={styles.headerActions}>
                            <Tooltip label={emailLabel}>
                                <Clickable
                                    href={mailtoLink}
                                    className={styles.headerActionButton}
                                >
                                    <Svg
                                        name="mail"
                                        className={styles.headerActionSvg}
                                        visuallyHiddenText={emailLabel}
                                    />
                                </Clickable>
                            </Tooltip>
                            <Tooltip label={printLabel}>
                                <Clickable
                                    onPress={printPage}
                                    className={styles.headerActionButton}
                                >
                                    <Svg
                                        name="print"
                                        className={styles.headerActionSvg}
                                        visuallyHiddenText={printLabel}
                                    />
                                </Clickable>
                            </Tooltip>
                        </div>
                    }
                />
                <div className={styles.pageInner}>
                    <SpokeContributionsList
                        suite={state.suite}
                        dispatch={dispatch}
                        mode="landing"
                    />
                </div>
            </Themed>
            <ConnectDealerModal
                isOpen={dealerModalOpen}
                onClose={() => setDealerModalOpen(false)}
            />
        </section>
    );
};
